import type { Dm2016ReferenceInterpretationMode } from "@rcprofessionale/calc-core";

import {
  calculateDm2016LinkedBundleWithHashes,
  getDm2016LabCatalog,
  getDm2016LinkedServices,
  type HashedDm2016LinkedBundleCalculationResult,
} from "./dm2016-application.js";

export interface Dm2016LinkedLabOptions {
  readonly onInvalid?: () => void;
  readonly onResult?: (result: HashedDm2016LinkedBundleCalculationResult) => void;
}

export interface Dm2016LinkedLabController {
  setEnabled(enabled: boolean, reason?: string): void;
}

function option(value: string, label: string): HTMLOptionElement {
  const element = document.createElement("option");
  element.value = value;
  element.textContent = label;
  return element;
}

function euro(value: string): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function decimalInput(input: HTMLInputElement): string {
  return input.value.trim().replace(",", ".");
}

export function mountDm2016LinkedLab(
  root: HTMLElement,
  options: Dm2016LinkedLabOptions = {},
): Dm2016LinkedLabController | null {
  const section = document.createElement("section");
  section.className = "dm2016-linked wrap";
  section.setAttribute("aria-labelledby", "dm2016-linked-title");
  section.innerHTML = `
    <div class="dm2016-linked__heading">
      <div>
        <span class="eyebrow">3 · Prestazioni collegate</span>
        <h2 id="dm2016-linked-title">Le note diventano domande semplici.</h2>
        <p>Attiva soltanto le attività presenti nell’incarico. Il sistema aggiunge le righe dipendenti e applica ogni effetto esclusivamente alla prestazione corretta.</p>
      </div>
      <div class="dm2016-linked__assurance"><span aria-hidden="true">✓</span><p><strong>Controllo incrociato</strong>Valori derivati, totale e tracce vengono ricalcolati e firmati dal server prima del PDF.</p></div>
    </div>
    <details class="dm2016-linked__disclosure">
      <summary><span><strong>Aggiungi prestazioni collegate</strong><small>Varianti, collaudi e certificazione energetica</small></span></summary>
      <form class="dm2016-linked__form" data-linked-form novalidate>
      <div class="dm2016-linked__destination">
        <label>Categoria dell’opera<select data-linked-category></select></label>
        <label>Destinazione funzionale<select data-linked-destination></select><small data-linked-destination-detail></small></label>
        <details class="dm2016-lab__advanced"><summary>Metodo di calcolo avanzato</summary><label>Modalità di lettura<select data-linked-mode><option value="printed_source_strict">Default normativo · testo stampato</option><option value="professionearchitetto_v0_74">Compatibilità CNI / ProfessioneArchitetto v0.74</option></select></label></details>
      </div>

      <fieldset class="dm2016-linked__card" data-linked-card="variation">
        <label class="dm2016-linked__toggle"><input type="checkbox" data-linked-toggle="variation" /><span><strong>Variante in corso d’opera</strong><small>QcI.07 · nota 10</small></span></label>
        <div class="dm2016-linked__fields" data-linked-fields="variation" hidden>
          <label>Quantità assolute in aumento (€)<input type="number" min="0" step="0.01" inputmode="decimal" data-linked-increase value="120000" /></label>
          <label>Quantità assolute in diminuzione (€)<input type="number" min="0" step="0.01" inputmode="decimal" data-linked-decrease value="30000" /></label>
          <label class="dm2016-linked__subtoggle"><input type="checkbox" data-linked-toggle="new-works" /><span><strong>Sono presenti nuove opere o nuovi prezzi</strong><small>Aggiunge QcI.08 · nota 11</small></span></label>
          <label data-linked-field="new-works" hidden>Importo lordo delle nuove opere (€)<input type="number" min="0.01" step="0.01" inputmode="decimal" data-linked-new-works value="80000" /></label>
        </div>
      </fieldset>

      <fieldset class="dm2016-linked__card" data-linked-card="collaudo">
        <label class="dm2016-linked__toggle"><input type="checkbox" data-linked-toggle="collaudo" /><span><strong>Collaudo tecnico-amministrativo</strong><small>QdI.01 · nota 12</small></span></label>
        <div class="dm2016-linked__fields" data-linked-fields="collaudo" hidden>
          <label>Valore dell’opera per il collaudo (€)<input type="number" min="0.01" step="0.01" inputmode="decimal" data-linked-collaudo-value value="1000000" /></label>
          <label>Quando avviene il collaudo?<select data-linked-collaudo-timing><option value="completed">A lavori ultimati</option><option value="in_progress">In corso d’opera · maggiorazione 20%</option></select></label>
        </div>
      </fieldset>

      <fieldset class="dm2016-linked__card" data-linked-card="energy">
        <label class="dm2016-linked__toggle"><input type="checkbox" data-linked-toggle="energy" /><span><strong>Certificazione energetica</strong><small>QdI.05 · nota 13</small></span></label>
        <div class="dm2016-linked__fields" data-linked-fields="energy" hidden>
          <label>Valore dell’opera per l’attestato (€)<input type="number" min="0.01" step="0.01" inputmode="decimal" data-linked-energy-value value="1000000" /></label>
          <label>La diagnosi energetica è già disponibile?<select data-linked-diagnosis-status><option value="available">Sì, è disponibile</option><option value="absent">No, deve essere redatta</option></select></label>
          <label data-linked-field="diagnosis" hidden>Valore V della diagnosi energetica (€)<input type="number" min="0.01" step="0.01" inputmode="decimal" data-linked-diagnosis-value value="600000" /><small>Il sistema aggiunge automaticamente QbII.22 come riga separata.</small></label>
        </div>
      </fieldset>

      <div class="dm2016-linked__submit">
        <p>Seleziona almeno una prestazione. Nessun valore viene dedotto da una riga diversa.</p>
        <button type="submit">Calcola le prestazioni collegate</button>
      </div>
      <p class="dm2016-lab__error" data-linked-error role="alert" hidden></p>
      </form>
      <section class="dm2016-linked__result" data-linked-result hidden aria-live="polite">
      <header><div><span>Compenso parametrico complessivo</span><strong data-linked-total></strong></div><p>Somma sui valori non arrotondati delle singole prestazioni.</p></header>
      <details class="dm2016-linked__result-details">
        <summary>Vedi dettaglio delle prestazioni e impronta</summary>
        <div class="dm2016-linked__table-scroll"><table><thead><tr><th>Prestazione</th><th>Valore V</th><th>Nota</th><th>Moltiplicatore</th><th>Compenso</th></tr></thead><tbody data-linked-rows></tbody></table></div>
        <footer><span data-linked-source></span><code data-linked-hash></code></footer>
      </details>
      </section>
    </details>`;
  root.append(section);

  const catalog = getDm2016LabCatalog();
  const form = section.querySelector<HTMLFormElement>("[data-linked-form]");
  const category = section.querySelector<HTMLSelectElement>("[data-linked-category]");
  const destination = section.querySelector<HTMLSelectElement>("[data-linked-destination]");
  const destinationDetail = section.querySelector<HTMLElement>("[data-linked-destination-detail]");
  const mode = section.querySelector<HTMLSelectElement>("[data-linked-mode]");
  const error = section.querySelector<HTMLElement>("[data-linked-error]");
  const resultSection = section.querySelector<HTMLElement>("[data-linked-result]");
  const total = section.querySelector<HTMLElement>("[data-linked-total]");
  const rows = section.querySelector<HTMLTableSectionElement>("[data-linked-rows]");
  const source = section.querySelector<HTMLElement>("[data-linked-source]");
  const hash = section.querySelector<HTMLElement>("[data-linked-hash]");
  if (
    form === null || category === null || destination === null
    || destinationDetail === null || mode === null || error === null
    || resultSection === null || total === null || rows === null
    || source === null || hash === null
  ) return null;

  let calculationEnabled = false;
  const controller: Dm2016LinkedLabController = {
    setEnabled(enabled, reason) {
      calculationEnabled = enabled;
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>(
        "input, select, button",
      ).forEach((control) => {
        control.disabled = !enabled;
      });
      if (!enabled) {
        resultSection.hidden = true;
        error.textContent = reason
          ?? "Il percorso D.M. 2016 non è autorizzato dallo screening corrente.";
        error.hidden = false;
      } else {
        error.hidden = true;
      }
    },
  };

  const checkbox = (name: string): HTMLInputElement =>
    section.querySelector<HTMLInputElement>(`[data-linked-toggle="${name}"]`)!;
  const input = (selector: string): HTMLInputElement =>
    section.querySelector<HTMLInputElement>(selector)!;
  const select = (selector: string): HTMLSelectElement =>
    section.querySelector<HTMLSelectElement>(selector)!;
  const variation = checkbox("variation");
  const newWorks = checkbox("new-works");
  const collaudo = checkbox("collaudo");
  const energy = checkbox("energy");
  const diagnosisStatus = select("[data-linked-diagnosis-status]");
  const diagnosisAbsentOption = diagnosisStatus.querySelector<HTMLOptionElement>(
    "option[value='absent']",
  );

  const invalidate = (): void => {
    resultSection.hidden = true;
    options.onInvalid?.();
  };
  const updateConditionalFields = (): void => {
    section.querySelector<HTMLElement>("[data-linked-fields='variation']")!.hidden = !variation.checked;
    if (!variation.checked) newWorks.checked = false;
    newWorks.disabled = !variation.checked
      || !getDm2016LinkedServices(destination.value)
        .some((service) => service.code === "QcI.08");
    section.querySelector<HTMLElement>("[data-linked-field='new-works']")!.hidden = !newWorks.checked;
    section.querySelector<HTMLElement>("[data-linked-fields='collaudo']")!.hidden = !collaudo.checked;
    section.querySelector<HTMLElement>("[data-linked-fields='energy']")!.hidden = !energy.checked;
    section.querySelector<HTMLElement>("[data-linked-field='diagnosis']")!.hidden =
      !energy.checked || diagnosisStatus.value !== "absent";
  };
  const updateServiceAvailability = (): void => {
    const available = new Set(
      getDm2016LinkedServices(destination.value).map((service) => service.code),
    );
    const setCardAvailability = (
      cardName: "variation" | "collaudo" | "energy",
      toggle: HTMLInputElement,
      serviceCode: "QcI.07" | "QdI.01" | "QdI.05",
    ): void => {
      const card = section.querySelector<HTMLElement>(`[data-linked-card="${cardName}"]`)!;
      const enabled = available.has(serviceCode);
      card.hidden = !enabled;
      toggle.disabled = !enabled;
      if (!enabled) toggle.checked = false;
    };
    setCardAvailability("variation", variation, "QcI.07");
    setCardAvailability("collaudo", collaudo, "QdI.01");
    setCardAvailability("energy", energy, "QdI.05");
    newWorks.disabled = !variation.checked || !available.has("QcI.08");
    if (newWorks.disabled) newWorks.checked = false;
    if (diagnosisAbsentOption !== null) {
      diagnosisAbsentOption.disabled = !available.has("QbII.22");
      if (diagnosisAbsentOption.disabled && diagnosisStatus.value === "absent") {
        diagnosisStatus.value = "available";
      }
    }
    updateConditionalFields();
  };
  section.querySelectorAll<HTMLInputElement>("[data-linked-toggle]").forEach((toggle) => {
    toggle.addEventListener("change", () => {
      updateConditionalFields();
      invalidate();
    });
  });
  diagnosisStatus.addEventListener("change", () => {
    updateConditionalFields();
    invalidate();
  });

  const applicableCategories = catalog.categories.filter((item) =>
    item.destinations.some((candidate) => getDm2016LinkedServices(candidate.code).length > 0)
  );
  category.replaceChildren(...applicableCategories.map((item) =>
    option(item.code, `${item.label} · ${item.code}`)
  ));
  const updateDestinations = (): void => {
    const selected = applicableCategories.find((item) => item.code === category.value);
    const destinations = (selected?.destinations ?? []).filter(
      (item) => getDm2016LinkedServices(item.code).length > 0,
    );
    destination.replaceChildren(...destinations.map((item) =>
      option(item.code, `${item.code} · G ${item.complexityG}`)
    ));
    const current = destinations.find((item) => item.code === destination.value);
    destinationDetail.textContent = current?.description ?? "";
    updateServiceAvailability();
    invalidate();
  };
  category.addEventListener("change", updateDestinations);
  destination.addEventListener("change", () => {
    const current = catalog.categories.flatMap((item) => item.destinations)
      .find((item) => item.code === destination.value);
    destinationDetail.textContent = current?.description ?? "";
    updateServiceAvailability();
    invalidate();
  });
  form.addEventListener("input", (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === "checkbox") return;
    invalidate();
  });
  mode.addEventListener("change", invalidate);
  updateDestinations();

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    error.hidden = true;
    try {
      if (!calculationEnabled) {
        throw new Error("Il percorso D.M. 2016 non è autorizzato dallo screening corrente.");
      }
      const services: Array<{ serviceCode: "QcI.07" | "QcI.08" | "QdI.01" | "QdI.05" | "QbII.22"; valueV?: string }> = [];
      const footnoteContext: {
        variationAbsoluteIncrease?: string;
        variationAbsoluteDecrease?: string;
        newlyDesignedWorksGrossValue?: string;
        collaudoInCorsoDOpera?: boolean;
        energyDiagnosisDocumentationAvailable?: boolean;
      } = {};
      if (variation.checked) {
        services.push({ serviceCode: "QcI.07" });
        footnoteContext.variationAbsoluteIncrease = decimalInput(input("[data-linked-increase]"));
        footnoteContext.variationAbsoluteDecrease = decimalInput(input("[data-linked-decrease]"));
        if (newWorks.checked) {
          services.push({ serviceCode: "QcI.08" });
          footnoteContext.newlyDesignedWorksGrossValue = decimalInput(input("[data-linked-new-works]"));
        }
      }
      if (collaudo.checked) {
        services.push({
          serviceCode: "QdI.01",
          valueV: decimalInput(input("[data-linked-collaudo-value]")),
        });
        footnoteContext.collaudoInCorsoDOpera =
          select("[data-linked-collaudo-timing]").value === "in_progress";
      }
      if (energy.checked) {
        services.push({
          serviceCode: "QdI.05",
          valueV: decimalInput(input("[data-linked-energy-value]")),
        });
        const diagnosisAvailable = diagnosisStatus.value === "available";
        footnoteContext.energyDiagnosisDocumentationAvailable = diagnosisAvailable;
        if (!diagnosisAvailable) {
          services.push({
            serviceCode: "QbII.22",
            valueV: decimalInput(input("[data-linked-diagnosis-value]")),
          });
        }
      }
      const calculation = await calculateDm2016LinkedBundleWithHashes({
        destinationCode: destination.value,
        services,
        footnoteContext,
        interpretationMode: mode.value as Dm2016ReferenceInterpretationMode,
      });
      total.textContent = euro(calculation.compensationSummary.compensationRounded);
      rows.replaceChildren(...calculation.references.map((reference, index) => {
        const resolved = calculation.footnoteResolution.services[index]!;
        const row = document.createElement("tr");
        const values = [
          `${reference.resolution.serviceCode} · ${calculation.serviceLabels[reference.resolution.serviceCode as keyof typeof calculation.serviceLabels]}`,
          euro(reference.calculation.valueV),
          resolved.appliedFootnotes.length === 0
            ? "—"
            : resolved.appliedFootnotes.map((note) => `n. ${note}`).join(", "),
          reference.calculation.multiplier === "1" ? "—" : `× ${reference.calculation.multiplier}`,
          euro(reference.calculation.compensationRounded),
        ];
        values.forEach((value) => {
          const cell = document.createElement("td");
          cell.textContent = value;
          row.append(cell);
        });
        return row;
      }));
      source.textContent = `${calculation.source.publication} · ${calculation.destination.code}`;
      hash.textContent = `${calculation.resultSha256.slice(0, 12)}…${calculation.resultSha256.slice(-10)}`;
      resultSection.hidden = false;
      options.onResult?.(calculation);
    } catch (cause) {
      resultSection.hidden = true;
      options.onInvalid?.();
      error.textContent = cause instanceof Error
        ? cause.message
        : "Il calcolo collegato è stato bloccato perché i dati non sono validi.";
      error.hidden = false;
    }
  });
  controller.setEnabled(false);
  return controller;
}
